import {isEnumValue} from '@Utils/enum.util';
import {getAjaxErrorCode} from '@Utils/ajax.util';
import {parseEachOrNull} from '@Components/ai-chat-window/lib/stream-component-prop-guards';
import {isPlainObject} from '@Utils/object.util';
import {PROPOSED_CHANGE_ACTION_ENDPOINT, ProposedChangeStatus, ProposedChangeTapOutcome, STALE_COMPONENT_ACTION_ERROR_CODE} from './proposed-change-card.types';
import type {ProposedChangeActionRequest, ProposedChangeCardProps, ProposedChangeLabels, ProposedChange, ProposedChangeTapResult, ProposedChangeValue} from './proposed-change-card.types';

export const parseProposedChangeCardProps = (props: Record<string, unknown>): ProposedChangeCardProps | null => {
  const {title, appliedTitle, actionKey, status} = props;
  const rows = Array.isArray(props.rows) ? parseEachOrNull(props.rows, parseRow) : null;
  const labels = parseLabels(props.labels);
  if (rows === null || rows.length === 0 || labels === null || !isEnumValue(ProposedChangeStatus, status)) {
    return null;
  }

  if (typeof title !== 'string' || typeof appliedTitle !== 'string' || typeof actionKey !== 'string') {
    return null;
  }

  return {
    title,
    appliedTitle,
    subtitle: stringOrNull(props.subtitle),
    rows,
    status,
    labels,
    editUrl: stringOrNull(props.editUrl),
    actionKey,
  };
};

/** Posts an apply or an undo tap. A refusal is an answer, not a throw: the card shows it. */
export const sendProposedChangeAction = async (request: ProposedChangeActionRequest): Promise<ProposedChangeTapResult> => {
  try {
    const response: unknown = await window.PMW.writeLocal(PROPOSED_CHANGE_ACTION_ENDPOINT, request);
    if (!isPlainObject(response) || !isEnumValue(ProposedChangeStatus, response.status)) {
      return {outcome: ProposedChangeTapOutcome.FAILED};
    }

    return {outcome: ProposedChangeTapOutcome.SETTLED, status: response.status};
  } catch (error: unknown) {
    return {outcome: getAjaxErrorCode(error) === STALE_COMPONENT_ACTION_ERROR_CODE ? ProposedChangeTapOutcome.STALE : ProposedChangeTapOutcome.FAILED};
  }
};

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const parseValue = (value: unknown): ProposedChangeValue | null => {
  if (!isPlainObject(value) || typeof value.text !== 'string') {
    return null;
  }

  return {text: value.text, swatchHex: stringOrNull(value.swatchHex), imageUrl: stringOrNull(value.imageUrl)};
};

const parseRow = (row: Record<string, unknown>): ProposedChange | null => {
  const after = parseValue(row.after);
  const before = row.before === null || row.before === undefined ? null : parseValue(row.before);
  const isMalformedBefore = before === null && row.before !== null && row.before !== undefined;
  if (typeof row.label !== 'string' || after === null || isMalformedBefore) {
    return null;
  }

  return {label: row.label, before, after};
};

const parseLabels = (labels: unknown): ProposedChangeLabels | null => {
  if (!isPlainObject(labels)) {
    return null;
  }

  const {apply, applied, undo, edit, stale, failed, undoFailed, readOnlyTitle} = labels;
  if (
    typeof apply !== 'string' ||
    typeof applied !== 'string' ||
    typeof undo !== 'string' ||
    typeof edit !== 'string' ||
    typeof stale !== 'string' ||
    typeof failed !== 'string' ||
    typeof undoFailed !== 'string' ||
    typeof readOnlyTitle !== 'string'
  ) {
    return null;
  }

  return {apply, applied, undo, edit, stale, failed, undoFailed, readOnlyTitle};
};
